<template>
    <div class="table-responsive">
        <table class="table b-table table-hover bordered overview-table">
            <thead>
                <tr>
                    <th class="pl-1 pr-0">{{ $t('Component(Grid):column-name') }}</th>
                    <th v-for="field in activeFields">
                        <div>{{ field.label }}</div>
                    </th>
                </tr>
            </thead>
            <tbody v-if="search" class="column-search">
                <tr>
                    <td>
                        <slot name="filter-name">
                            <b-form-input
                                id="helperInput"
                                :placeholder="$t('Component(Grid):column-name')"
                            />
                        </slot>
                    </td>
                    <td v-for="field in activeFields">
                        <slot :name="'filter-' + field.key" :field="field">
                            <div></div>
                        </slot>
                    </td>
                </tr>
            </tbody>
            <tbody v-if="list.length === 0">
                <tr>
                    <td :colspan="activeFields.length + 1">
                        <div class="p-4 d-flex align-items-center justify-content-center" :style="minHeight">
                            <b>Nothing found</b>
                        </div>
                    </td>
                </tr>
            </tbody>
            <tbody v-for="(product, index) in this.list" :id="'p-' + product.id + '-index-' + index">
                <tr
                    @click="(e) => onRowClick(product, index, e)"
                    v-if="!product.loading && (product.level === 0 || (isProductParentExpanded(product) && isProductChildVisible(product)))"
                    :class="{'active-branch-row': isInActiveBranch(product)}"
                >
                    <td class="pr-0 hierarchy-control" :style="{'padding-left': (product.level * 18 + 14) + 'px', 'display': 'flex'}">
                        <div @click.stop="toggleChildren(product, product, index)">
                            <div style="margin-right: 8px;">
                                <feather-icon v-if="showFolderIcon(product)" :size="iconSize" :icon="isProductExpanded(product) ? 'FolderMinusIcon' : 'FolderPlusIcon'" />
                                <feather-icon v-else :size="iconSize" icon="FileIcon" />
                            </div>
                        </div>
                        <div class="product-name" style="padding: 2px 8px 0 0" :title="nameTitle">
                            {{ getName(product) }}
                        </div>
                    </td>
                    <slot name="row" :item="product" :activeFields="activeFields">
                        <td v-for="field in activeFields">
                            <slot :name="'cell-' + field.key" :item="product" :field="field">
                                <div :title="field.title" v-html="renderField(field, product)"></div>
                            </slot>
                        </td>
                    </slot>
                </tr>
                <tr v-else-if="product.loading && (isProductParentExpanded(product) || product.toggle)">
                    <td :colspan="activeFields.length + 1">
                        <Overlay :show="showLoading">
                            {{ $t('Component(Text):loading') }}
                        </Overlay>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
/**
 * Use different states for filtering and usual-general state
 */
import {BFormInput} from 'bootstrap-vue'
import store from "@/store"
import Overlay from "@core/components/common/Overlay.vue";
import {ProductProvider} from "@core/domain/product";
import {ProductType} from "@/libs/product";

class TableState
{
    expanded = new Set();
    loadedChildren = new Set();
    loading = new Set();

    isExpanded(key) {
        return this.expanded.has(key);
    }

    setExpanded(key, value) {
        if (value) {
            this.expanded.add(key);
        } else {
            this.expanded.delete(key);
        }
    }

    isChildrenLoaded(key) {
        return this.loadedChildren.has(key);
    }

    markChildrenAsLoaded(key) {
        this.loadedChildren.add(key);
    }

    isLoading(key) {
        return this.loading.has(key);
    }

    setLoading(key, value) {
        if (value) {
            this.loading.add(key);
        } else {
            this.loading.delete(key);
        }
    }

    clearLoadingState() {
        this.loading.clear();
    }
}

let organizationId = store.state.user.organization.id;
const __state = {};
__state[organizationId] = {
    filtered: new TableState(),
    general: new TableState(),
};

const CACHE_TTL_MS = 5 * 60 * 1000;
const CHILDREN_CACHE = new Map();

function getCacheKey(orgId, id) {
    return orgId + ':' + id;
}

function getCachedChildren(orgId, id) {
    const entry = CHILDREN_CACHE.get(getCacheKey(orgId, id));
    if (!entry) {
        return null;
    }
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        CHILDREN_CACHE.delete(getCacheKey(orgId, id));
        return null;
    }
    return entry.data;
}

function setCachedChildren(orgId, id, children) {
    // CHILDREN_CACHE.set(getCacheKey(orgId, id), {ts: Date.now(), data: children});
}

function cacheSubtree(orgId, nodes) {
    // nodes.forEach((node) => {
    //     if (node && Array.isArray(node.children) && node.children.length > 0) {
    //         setCachedChildren(orgId, node.id, node.children);
    //         cacheSubtree(orgId, node.children);
    //     }
    // });
}

store.watch(
    (state) => {
        return state.user.organization;
    },
    () => {
        organizationId = store.state.user.organization.id;
        if (!__state.hasOwnProperty(organizationId)) {
            __state[organizationId] = {
                filtered: new TableState(),
                general: new TableState(),
            };
        }
    }
)

/**
 *
 * @returns TableState
 */
function getState(type) {
    return __state[organizationId][type];
}

export default {
    components: {
        Overlay,
        BFormInput
    },
    props: {
        isFilterActive: {
            default: false
        },
        nameRenderer: {
            default: null
        },
        nameTitle: {
            default: ''
        },
        search: {
            type: Boolean,
            default: false,
        },
        preloadLevel: {
            type: Number,
            default: 0,
        },
        activeFields: {
            type: Array,
            default() {
                return [];
            }
        },
        fields: {
            type: Array,
            default() {
                return [];
            }
        },
        children: {
            type: Array,
            default() {
                return [];
            }
        },
        nested: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            showExpandColumn: false,
            iconSize: '22',
            list: this.initList(this.children),
            showLoading: true,
            activeBranchId: null,
        }
    },
    methods: {
        showFolderIcon(product) {
            return product.hasChildren;
        },
        getName(product) {
            if (this.nameRenderer) {
                return this.nameRenderer.call(this, product);
            }
            return product.names.length > 0 ? product.names[0].value : '';
        },

        renderField(field, product) {
            if (field.renderer) {
                return field.renderer(product);
            }

            if (product.hasOwnProperty(field.key)) {
                return product[field.key];
            }

            return '';
        },

        isProductParentExpanded(product) {
            if (!product.hasOwnProperty('parent')) {
                if (product.loading) {
                    return product.item.__expanded;
                }
                return true;
            }
            return product.__parent.__expanded;
        },

        isProductExpanded(product) {
            return product.__expanded;
        },

        isProductChildVisible(product) {
            return this.isVisibleByAncestors(product);
        },

        isInActiveBranch(product) {
            if (!this.activeBranchId) {
                return false;
            }
            let current = product;
            while (current) {
                if (current.__id === this.activeBranchId) {
                    return true;
                }
                current = current.__parent;
            }
            return false;
        },

        isVisibleByAncestors(product) {
            let current = product.__parent;
            while (current) {
                if (!current.__expanded) {
                    return false;
                }
                current = current.__parent;
            }
            return true;
        },

        shouldAutoExpand(product) {
            const expandAutomatically = product.hasOwnProperty('expand') && product.expand === true;
            return expandAutomatically || product.type === ProductType.FICTITIOUS;
        },

        collapseChildren(product) {
            const state = getState(this.stateType);
            product.children.forEach(p => {
                p.__expanded = false;
                state.setExpanded(p.__id, false);
                this.$emit('collapsed', product, p);
                this.collapseChildren(p);
            });
        },

        applyAutoExpand(product) {
            const state = getState(this.stateType);
            product.children.forEach(child => {
                if (this.shouldAutoExpand(child)) {
                    child.__expanded = true;
                    state.setExpanded(child.__id, true);
                }

                if (child.__expanded) {
                    this.applyAutoExpand(child);
                }
            });
        },

        toggleChildren(product, child, index) {
            const state = getState(this.stateType);
            const toggle = () => {
                const nextExpanded = !child.__expanded;
                child.__expanded = nextExpanded;
                state.setExpanded(child.__id, nextExpanded);

                if (!child.__expanded) {
                    this.collapseChildren(child);
                    this.$emit('collapsed', product, child);
                    if (this.activeBranchId === child.__id) {
                        this.activeBranchId = null;
                    }
                } else {
                    this.applyAutoExpand(child);
                    this.$emit('expanded', product, child);
                    this.activeBranchId = child.__id;
                }
            };

            if (true === product.hasChildren && product.children.length === 0) {
                if (!state.isLoading(product.__id)) {
                    state.setLoading(product.__id, true);
                    let obj = {
                        loading: true,
                        item: product,
                        toggle: true
                    };
                    this.list.splice(index + 1, 0, obj);
                    this.loadData(product, index + 1, toggle);
                } else if (this.list[index + 1].loading) {
                    this.list[index + 1].toggle = !this.list[index + 1].toggle;

                    if (this.list[index + 1].promise) {
                        this.list[index + 1].promise.then(() => {
                            toggle();
                        });
                    }
                }
            } else {
                toggle();
            }
        },

        recursivelyCopyChildren(children) {
            return children.map(c => {
                return {
                    ...c,
                    children: this.recursivelyCopyChildren(c.children)
                }
            });
        },

        loadData(product, index, toggler) {
            const cached = getCachedChildren(organizationId, product.id);
            if (cached) {
                const state = getState(this.stateType);
                state.setLoading(product.__id, false);
                state.markChildrenAsLoaded(product.__id);
                this.list.splice(index, 1);
                this.updateList(product, this.recursivelyCopyChildren(cached), index);
                cacheSubtree(organizationId, cached);
                toggler();
                return;
            }

            ProductProvider(product.id)
                .getChildren(true)
                .then(children => {
                    const state = getState(this.stateType);
                    cacheSubtree(organizationId, children);
                    setCachedChildren(organizationId, product.id, children);
                    state.setLoading(product.__id, false);
                    state.markChildrenAsLoaded(product.__id);

                    this.list.splice(index, 1);
                    this.updateList(product, this.recursivelyCopyChildren(children), index);
                    toggler();
                })
            ;
        },

        onRowClick(product, index, event) {
            const targetClass = event.target.className;
            if (targetClass && targetClass.includes('hierarchy-control')) {
                if (this.showFolderIcon(product)) {
                    this.toggleChildren(product, product, index);
                    return;
                }
            }
            this.$emit('click', product);
        },

        initList(items) {
            const list = [];
            const roots = [];

            items.forEach((item) => {
                this.initListItem(item, null, 0, list);
                roots.push(item);
            });

            roots.forEach(root => {
                if (root.__expanded) {
                    this.applyAutoExpand(root);
                }
            });

            return list;
        },

        initListItem(item, parent, level, list) {
            item.level = level;
            if (parent) {
                item.parent = parent;
                this.$set(item, '__parent', parent);
                this.$set(item, '__id', parent.__id + '_' + item.id);
            } else {
                this.$set(item, '__id', item.id);
            }

            list.push(item);
            this.initItemState(item);

            item.children.forEach(child => {
                this.initListItem(child, item, level + 1, list);
            });
        },

        initItemState(item) {
            const state = getState(this.stateType);
            if (this.shouldAutoExpand(item)) {
                state.setExpanded(item.__id, true);
            }
            this.$set(item, '__expanded', state.isExpanded(item.__id));
        },

        updateList(product, children, index) {
            let list = [];
            product.children = children;
            children.forEach((item) => this.initListItem(item, product, product.level + 1, list));
            this.list.splice(index, 0, ...list);

            if (product.__expanded) {
                this.applyAutoExpand(product);
            }
        },

        restoreAndPreload() {
            const state = getState(this.stateType);
            const data = [];
            let needsRestore = false;
            this.list.forEach((item, index) => {
                if (item.loading) {
                    return;
                }

                if (!item.hasChildren || item.children.length > 0) {
                    return;
                }

                if (!(state.isChildrenLoaded(item.__id) || item.level < this.preloadLevel || state.isExpanded(item.__id))) {
                    return;
                }

                if (state.isLoading(item.__id)) {
                    return;
                }

                const cached = getCachedChildren(organizationId, item.id);
                if (cached) {
                    this.updateList(item, this.recursivelyCopyChildren(cached), index + 1);
                    cacheSubtree(organizationId, cached);
                    state.markChildrenAsLoaded(item.__id);
                    state.setLoading(item.__id, false);
                    item.__restored = true;
                    if (item.__expanded) {
                        this.applyAutoExpand(item);
                    }
                    needsRestore = true;
                    return;
                }

                const product = ProductProvider(item.id);
                const promise = product.getChildren(true);
                const obj = {
                    loading: true,
                    item: item,
                    toggle: false,
                    promise
                };
                this.list.splice(index + 1, 0, obj);

                state.setLoading(item.__id, true);
                data.push({
                    item,
                    promise
                });
            });

            data.forEach((dataItem) => {
                dataItem.promise.then(children => {
                    if (!state.isLoading(dataItem.item.__id)) {
                        return;
                    }

                    this.list.forEach((listItem, index) => {
                        if (listItem.loading && listItem.item.__id === dataItem.item.__id) {
                            this.list.splice(index, 1);
                        }
                    });

                    this.list.forEach((listItem, index) => {
                        if (listItem.__id !== dataItem.item.__id) {
                            return;
                        }
                        if (listItem.__restored) {
                            return;
                        }

                        this.updateList(dataItem.item, this.recursivelyCopyChildren(children), index + 1);
                    });

                    setCachedChildren(organizationId, dataItem.item.id, children);
                    state.markChildrenAsLoaded(dataItem.item.__id);
                    state.setLoading(dataItem.item.__id, false);
                    dataItem.item.__restored = true;

                    if (dataItem.item.__expanded) {
                        this.applyAutoExpand(dataItem.item);
                    }

                    this.restoreAndPreload();
                });
            });

            if (needsRestore) {
                this.restoreAndPreload();
            }
        }
    },
    watch: {
        children() {
            getState(this.stateType).clearLoadingState();
            this.list = this.initList(this.children);
            this.restoreAndPreload();
        },
    },

    computed: {
        stateType() {
            return this.isFilterActive ? 'filtered' : 'general';
        },
        tableStyles() {
            return 'min-height: ' + (window.innerHeight - 200) + 'px';
        },
        minHeight() {
    return 'min-height: ' + (window.innerHeight - 400) + 'px';
    }
}
};
</script>
<style>
table.overview-table tbody {
    border: none;
}
.overview-table tbody.column-search td {
    padding: 6px 10px 6px 10px;
    /*background-color: #f3f2f7;*/
}

table td.hierarchy-control {
    /*background-color: #fbfbfb;*/
    /*border-right: 1px solid #e9e9e9;*/
    padding-right: 0;
}

table.overview-table td, table.overview-table thead th {
    white-space: nowrap;
}

.hierarchy-control .arrow {
    margin-left: auto;
    margin-right: 8px;
}

.overview-table tr.active-branch-row td {
    background-color: #fffaf0;
}
</style>
